<template>
  <section>
    <div class="kpi-relatorio">
      <div class="titulo">
        <div class="margem">
          <h2>KPIs — gestão de produção (injeção)</h2>
        </div>
      </div>
      <div class="margem kpi-relatorio__body">
        <div class="kpi-abas" role="tablist" aria-label="Tipo de relatório">
          <button v-for="aba in abas" :key="aba.id" type="button" role="tab" class="kpi-abas__btn"
            :class="{ 'kpi-abas__btn--ativa': abaAtiva === aba.id }"
            :aria-selected="abaAtiva === aba.id ? 'true' : 'false'" @click="selecionarAba(aba.id)"> {{ aba.label }}
          </button>
        </div>
        <br>
        <!-- Filtros (padrão relatório: bloco retrátil) -->
        <div class="bloco kpi-filtros" :class="{ 'kpi-filtros--aberto': filtrosAbertos }">
          <button type="button" class="kpi-filtros__header" @click="filtrosAbertos = !filtrosAbertos">
            <span class="kpi-filtros__header-title">Filtros</span>
            <span class="kpi-filtros__chevron" aria-hidden="true">{{ filtrosAbertos ? "▲" : "▼" }}</span>
          </button>
          <div v-show="filtrosAbertos" class="kpi-filtros__body">
            <div class="grid-2 kpi-filtros__grid kpi-filtros__grid--datas">
              <div>
                <label for="kpi-dt-ini">Data inicial</label>
                <input id="kpi-dt-ini" type="date" v-model="startDt" />
              </div>
              <div>
                <label for="kpi-dt-fim">Data final</label>
                <input id="kpi-dt-fim" type="date" v-model="endDt" />
              </div>
            </div>
            <div class="kpi-filtros__atalhos linha alinha-v">
              <span class="fonte-fraca fonte-menor">Atalho:</span>
              <button type="button" class="acao-secundaria" :disabled="loading" @click="definirSemanaAtual"> Esta semana
                (seg. a dom.) </button>
            </div>
            <p class="kpi-filtros__dica-chips fonte-fraca fonte-menor"> Injetoras: toque para marcar ou desmarcar.
              Nenhuma selecionada = todas. </p>
            <div class="kpi-filtros__linha-injetoras">
              <!-- Mesmo padrão visual de SelectableTags da lib (.tags / .ativo em thalamus.scss) -->
              <div class="tags kpi-filtros__chips-wrap" role="group" aria-label="Filtrar por injetora">
                <a v-for="m in maquinas" :key="m.id" href="#" role="button"
                  :class="{ ativo: maquinasSelecionadasIds.includes(m.id) }"
                  :aria-pressed="maquinasSelecionadasIds.includes(m.id) ? 'true' : 'false'"
                  @click.prevent="toggleMaquinaChip(m.id)"> {{ m.nome }} </a>
              </div>
              <div class="kpi-filtros__acoes-direita linha alinha-v">
                <button type="button" :disabled="loading" @click="aplicarPeriodo">Aplicar filtro</button>
                <button type="button" class="acao-secundaria" :disabled="loading" @click="limparPeriodo">Limpar</button>
              </div>
            </div>
            <p v-if="erroDatas" class="kpi-filtros__erro">{{ erroDatas }}</p>
          </div>
        </div>
        <div v-if="apiError" class="bloco kpi-api-erro" role="alert"> {{ apiError }} </div>
        <div v-if="loading" class="margem-topo alinha-centro kpi-loading-wrap">
          <div class="loading">
            <div></div>
          </div>
          <p class="fonte-fraca margem-topo">Carregando indicadores…</p>
        </div>
        <div v-else-if="abaAtiva === 'resumo' && payload" class="margem-topo kpi-stack">
          <!-- Resumo -->
          <div class="bloco kpi-pane">
            <h3 class="kpi-secao-titulo">Resumo</h3>
            <p class="kpi-periodo-pill fonte-fraca">
              <span class="chip bg-cinza">{{ payload.period.startDt }}</span>
              <span class="kpi-periodo-pill__ate">até</span>
              <span class="chip bg-cinza">{{ payload.period.endDt }}</span>
              <span v-if="payload.totais.diasNoPeriodo" class="kpi-periodo-pill__meta"> · {{
                payload.totais.diasNoPeriodo }} dia(s) com movimento </span>
              <span v-for="fm in payload.filtroMaquinas || []" :key="fm.maquinaId"
                class="chip bg-cinza kpi-periodo-pill__maq"> {{ fm.maquinaNome }} </span>
            </p>
            <div class="grid-3 kpi-mosaic">
              <article class="kpi-tile kpi-tile--destaque">
                <div class="kpi-tile__label">Peças boas (total)</div>
                <div class="kpi-tile__value">{{ formatInt(payload.totais.pecasBoas) }}</div>
                <div class="kpi-tile__hint fonte-fraca">Soma dos incrementos no período</div>
              </article>
              <article class="kpi-tile">
                <div class="kpi-tile__label">Média peças boas / dia</div>
                <div class="kpi-tile__value">{{ formatInt(payload.totais.mediaPecasBoasPorDia) }}</div>
              </article>
              <article class="kpi-tile">
                <div class="kpi-tile__label">Perdas (kg / 1000 peças boas)</div>
                <div class="kpi-tile__value">{{ formatDecimal(payload.totais.taxaMaterialKgPorMilPecas, 2) }}</div>
                <div class="kpi-tile__hint fonte-fraca">Refugo + borra + galho</div>
              </article>
              <article class="kpi-tile kpi-tile--perda">
                <div class="kpi-tile__label">Refugo</div>
                <div class="kpi-tile__value">{{ formatKg(payload.totais.kgRefugo) }} <span
                    class="kpi-tile__unit">kg</span></div>
              </article>
              <article class="kpi-tile kpi-tile--perda">
                <div class="kpi-tile__label">Borra</div>
                <div class="kpi-tile__value">{{ formatKg(payload.totais.kgBorra) }} <span
                    class="kpi-tile__unit">kg</span>
                </div>
              </article>
              <article class="kpi-tile kpi-tile--perda">
                <div class="kpi-tile__label">Galho</div>
                <div class="kpi-tile__value">{{ formatKg(payload.totais.kgGalho) }} <span
                    class="kpi-tile__unit">kg</span>
                </div>
              </article>
            </div>
          </div>
          <!-- Gráficos -->
          <div class="bloco kpi-pane">
            <h3 class="kpi-secao-titulo">Gráficos</h3>
            <div class="grid-2 kpi-charts-grid">
              <div class="kpi-chart-card">
                <h3 class="kpi-chart-card__title">Produção por dia (peças boas)</h3>
                <div class="kpi-chart-card__plot">
                  <Bar :data="productionDayChart" :options="productionDayBarOptions" />
                </div>
              </div>
              <div class="kpi-chart-card">
                <h3 class="kpi-chart-card__title">Produção por máquina e dia</h3>
                <div class="kpi-chart-card__plot kpi-chart-card__plot--tall">
                  <Bar :data="machineDayChart" :options="groupedBarOptions" />
                </div>
              </div>
              <div class="kpi-chart-card kpi-chart-card--wide">
                <h3 class="kpi-chart-card__title">Participação por tipo de perda (%)</h3>
                <div class="kpi-chart-card__plot">
                  <Pie :data="participacaoChartData" :options="participacaoPieOptions" />
                </div>
              </div>
            </div>
          </div>
          <!-- Tabela -->
          <div class="bloco kpi-pane kpi-pane--table">
            <h3 class="kpi-secao-titulo">Por máquina</h3>
            <div class="kpi-table-scroll">
              <table class="tabela">
                <thead>
                  <tr>
                    <th>Máquina</th>
                    <th class="alinha-direita">Peças boas</th>
                    <th class="alinha-direita">Refugo (kg)</th>
                    <th class="alinha-direita">Borra (kg)</th>
                    <th class="alinha-direita">Galho (kg)</th>
                    <th class="alinha-direita">Utilização (%)</th>
                    <th class="alinha-direita">Aderência (%)</th>
                    <th class="alinha-direita">Peças / h rodando</th>
                    <th class="alinha-direita">Perdas (kg / 1000 peças boas)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="m in payload.porMaquina" :key="m.maquinaId">
                    <td class="fonte-forte">{{ m.maquinaNome }}</td>
                    <td class="alinha-direita">{{ formatInt(m.pecasBoas) }}</td>
                    <td class="alinha-direita">{{ formatKg(m.kgRefugo) }}</td>
                    <td class="alinha-direita">{{ formatKg(m.kgBorra) }}</td>
                    <td class="alinha-direita">{{ formatKg(m.kgGalho) }}</td>
                    <td class="alinha-direita">{{ formatDecimal(m.utilizacaoPct, 1) }}</td>
                    <td class="alinha-direita">{{ formatDecimal(m.aderenciaPct, 1) }}</td>
                    <td class="alinha-direita">{{ formatDecimal(m.pecasBoasPorHoraRodando, 1) }}</td>
                    <td class="alinha-direita">{{ formatDecimal(m.kgPorMilPecas, 2) }}</td>
                  </tr>
                  <tr v-if="!payload.porMaquina.length">
                    <td colspan="9" class="alinha-centro" style="padding: 2rem">Nenhum dado no período</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="kpi-footnote fonte-fraca fonte-menor margem-topo">
              <strong>Utilização</strong> — entre o tempo em que a máquina estava efetivamente produzindo (rodando) e o
              tempo total contabilizado no período (rodando + paradas), qual parte foi de produção ativa. Considera
              apenas trabalhos de injeção já encerrados no intervalo de datas.<br />
              <strong>Aderência</strong> — compara as peças boas que saíram na máquina com a quantidade total planejada
              nas ordens de produção em que houve apontamento nessa máquina; indica se o volume produzido ficou próximo
              do previsto para essas ordens.
            </p>
          </div>
        </div>
        <div v-else-if="abaAtiva === 'indicadores-injecao'" class="kpi-relatorio-tab">
          <RelatorioIndicadoresInjecaoDashboard :payload="relatorioInjecao" />
        </div>
        <div v-else-if="abaAtiva === 'usinagens'" class="kpi-relatorio-tab">
          <RelatorioUsinagensDashboard :payload="relatorioUsinagens" />
        </div>
      </div>
    </div>
  </section>
</template>
<script>
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
} from "chart.js";
import { Bar, Pie } from "vue-chartjs";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  fetchGestaoProducaoKpis,
  fetchMaquinasKpi,
  fetchRelatorioIndicadoresInjecao,
  fetchRelatorioUsinagens,
} from "@/data/datasource/GestaoProducaoKpiDatasource";
import RelatorioIndicadoresInjecaoDashboard from "@/components/kpi/RelatorioIndicadoresInjecaoDashboard.vue";
import RelatorioUsinagensDashboard from "@/components/kpi/RelatorioUsinagensDashboard.vue";

/** Valores estampados no meio da barra (chartjs-plugin-datalabels) */
const kpiDatalabelsNaBarra = (formatter) => ({
  color: "#ffffff",
  font: { weight: "bold", size: 12 },
  anchor: "center",
  align: "center",
  formatter: formatter || ((v) => (v == null || v === "" ? "" : String(v))),
});

ChartJS.register(Title, Tooltip, Legend, BarElement, ArcElement, CategoryScale, LinearScale, ChartDataLabels);

export default {
  name: "GestaoProducaoKpis",
  components: {
    Bar,
    Pie,
    RelatorioIndicadoresInjecaoDashboard,
    RelatorioUsinagensDashboard,
  },
  data() {
    return {
      abas: [
        { id: "resumo", label: "Resumo geral" },
        { id: "indicadores-injecao", label: "Indicadores de produção" },
        { id: "usinagens", label: "Relatório de usinagens" },
      ],
      abaAtiva: "resumo",
      filtrosAbertos: true,
      startDt: "",
      endDt: "",
      /** ids selecionados nos chips; vazio = todas as injetoras na consulta */
      maquinasSelecionadasIds: [],
      maquinas: [],
      loading: false,
      apiError: "",
      erroDatas: "",
      payload: null,
      relatorioInjecao: null,
      relatorioUsinagens: null,
      productionDayChart: { labels: [], datasets: [] },
      machineDayChart: { labels: [], datasets: [] },
      participacaoChartData: { labels: [], datasets: [] },
      productionDayBarOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          datalabels: kpiDatalabelsNaBarra((v) =>
            v == null ? "" : new Intl.NumberFormat("pt-BR").format(Math.round(Number(v)))
          ),
        },
      },
      groupedBarOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: "top",
          },
          datalabels: kpiDatalabelsNaBarra((v) => {
            if (v == null || Number(v) === 0) return null;
            return new Intl.NumberFormat("pt-BR").format(Math.round(Number(v)));
          }),
        },
      },
      participacaoPieOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: "right",
          },
          datalabels: kpiDatalabelsNaBarra((v) =>
            v == null ? "" : `${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
          ),
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const nome = ctx.label || "";
                const val = ctx.raw;
                return `${nome}: ${Number(val).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
              },
            },
          },
        },
      },
    };
  },
  async created() {
    this.definirPeriodoPadraoUltimosDias(7);
    await this.carregarMaquinas();
    await this.aplicarPeriodo();
  },
  methods: {
    fmtDataLocal(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    definirPeriodoPadraoUltimosDias(dias) {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - (dias - 1));
      this.startDt = this.fmtDataLocal(start);
      this.endDt = this.fmtDataLocal(end);
    },
    definirSemanaAtual() {
      const hoje = new Date();
      const diaSemana = hoje.getDay() === 0 ? 7 : hoje.getDay();
      const segunda = new Date(hoje);
      segunda.setDate(hoje.getDate() - (diaSemana - 1));
      const domingo = new Date(segunda);
      domingo.setDate(segunda.getDate() + 6);
      this.startDt = this.fmtDataLocal(segunda);
      this.endDt = this.fmtDataLocal(domingo);
    },
    async aplicarPeriodo() {
      this.erroDatas = "";
      this.apiError = "";
      if (!this.startDt || !this.endDt) {
        this.erroDatas = "Informe data inicial e final.";
        return;
      }
      if (this.startDt > this.endDt) {
        this.erroDatas = "A data inicial não pode ser maior que a final.";
        return;
      }
      this.loading = true;
      this.payload = null;
      this.relatorioInjecao = null;
      this.relatorioUsinagens = null;
      try {
        const [resumo, injecao, usinagens] = await Promise.all([
          fetchGestaoProducaoKpis(this.startDt, this.endDt, this.maquinasSelecionadasIds),
          fetchRelatorioIndicadoresInjecao(this.startDt, this.endDt, this.maquinasSelecionadasIds),
          fetchRelatorioUsinagens(this.startDt, this.endDt),
        ]);
        this.payload = resumo;
        this.relatorioInjecao = injecao;
        this.relatorioUsinagens = usinagens;
        this.buildCharts();
      } catch (e) {
        const body = e?.response?.data;
        let msg =
          body?.error ||
          body?.message ||
          e?.message ||
          "Não foi possível carregar os indicadores.";
        if (body?.errors && typeof body.errors === "object") {
          const first = Object.values(body.errors)[0];
          if (Array.isArray(first) && first[0]) {
            msg = first[0];
          }
        }
        this.apiError = typeof msg === "string" ? msg : JSON.stringify(msg);
        this.payload = null;
        this.relatorioInjecao = null;
        this.relatorioUsinagens = null;
      } finally {
        this.loading = false;
      }
    },
    limparPeriodo() {
      this.erroDatas = "";
      this.apiError = "";
      this.maquinasSelecionadasIds = [];
      this.abaAtiva = "resumo";
      this.definirPeriodoPadraoUltimosDias(7);
      this.aplicarPeriodo();
    },
    selecionarAba(id) {
      this.abaAtiva = id;
    },
    toggleMaquinaChip(maquinaId) {
      const id = Number(maquinaId);
      if (Number.isNaN(id)) return;
      const idx = this.maquinasSelecionadasIds.indexOf(id);
      if (idx === -1) {
        this.maquinasSelecionadasIds = [...this.maquinasSelecionadasIds, id];
      } else {
        this.maquinasSelecionadasIds = this.maquinasSelecionadasIds.filter((x) => x !== id);
      }
    },
    async carregarMaquinas() {
      try {
        this.maquinas = await fetchMaquinasKpi();
      } catch {
        this.maquinas = [];
      }
    },
    buildCharts() {
      const p = this.payload;
      if (!p) return;

      this.productionDayChart = {
        labels: p.chartLabelsDia,
        datasets: [
          {
            label: "Peças boas",
            data: p.serieDiaria.map((s) => s.pecasBoas),
            backgroundColor: "hsl(216 64% 52%)",
          },
        ],
      };

      const cores = ["hsl(216 64% 52%)", "hsl(130 64% 42%)", "hsl(32 75% 48%)"];
      this.machineDayChart = {
        labels: p.chartLabelsDia,
        datasets: p.serieMaquinaDia.map((s, i) => ({
          label: s.maquinaNome,
          data: s.data,
          backgroundColor: cores[i % cores.length],
        })),
      };

      const part = p.totais.participacaoPct;
      this.participacaoChartData = {
        labels: ["Refugo", "Borra", "Galho"],
        datasets: [
          {
            label: "Participação",
            data: [part.refugo, part.borra, part.galho],
            backgroundColor: ["#5c5cad", "#c94c4c", "#b8860b"],
          },
        ],
      };
    },
    formatInt(n) {
      return new Intl.NumberFormat("pt-BR").format(Math.round(n ?? 0));
    },
    formatKg(n) {
      return new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n ?? 0);
    },
    formatDecimal(n, frac) {
      return new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: frac,
        maximumFractionDigits: frac,
      }).format(n ?? 0);
    },
  },
};
</script>
<style scoped>
/* Ocupa toda a largura horizontal da área principal (flex), sem cap artificial */
.kpi-relatorio {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  flex: 1 1 auto;
  align-self: stretch;
}

.kpi-relatorio__subtitle {
  margin-top: -8px;
  margin-bottom: 0;
}

.kpi-abas {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 0;
}

.kpi-relatorio-tab {
  margin-top: 0;
}

.kpi-relatorio-tab :deep(.rel-dash) {
  gap: var(--margem);
}

.kpi-relatorio-tab :deep(.rel-dash__actions) {
  margin-top: 0;
}

.kpi-abas__btn {
  border: 1px solid var(--cor-separador);
  background: var(--cor-bg);
  color: var(--cor-fonte-forte);
  padding: 8px 14px;
  border-radius: 999px;
  cursor: pointer;
  font: inherit;
  font-size: 0.9rem;
}

.kpi-abas__btn--ativa {
  background: var(--cor-primaria);
  border-color: var(--cor-primaria);
  color: #fff;
  font-weight: 600;
}

.kpi-relatorio__body {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.kpi-relatorio__body>.bloco+.bloco,
.kpi-relatorio__body>.bloco+.kpi-relatorio-tab,
.kpi-relatorio__body>.bloco+.margem-topo {
  margin-top: var(--margem);
}

.kpi-relatorio__body>.kpi-relatorio-tab {
  margin-top: 0;
}

.kpi-stack {
  display: flex;
  flex-direction: column;
  gap: var(--margem);
}

.kpi-stack>.bloco {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.kpi-secao-titulo {
  margin: 0 0 calc(var(--margem) * 0.75);
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--cor-fonte-forte);
  padding-bottom: 8px;
  border-bottom: 1px solid var(--cor-separador);
}

.margem-topo {
  margin-top: var(--margem);
}

.kpi-api-erro {
  border-left: 4px solid var(--cor-erro);
  padding: 12px 16px;
  color: var(--cor-erro);
  font-size: 0.95rem;
}

.kpi-loading-wrap {
  padding: var(--margem) 0;
}

/* Filtros retráteis */
.kpi-filtros {
  padding: 0;
  overflow: visible;
}

.kpi-filtros__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 18px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: var(--cor-fonte-forte);
  border-radius: 12px;
}

.kpi-filtros__header:hover {
  background: var(--cor-primaria-fraca);
}

.kpi-filtros__header-title {
  font-weight: 700;
  font-size: 1.05rem;
}

.kpi-filtros__chevron {
  color: var(--cor-fonte-fraca);
  font-size: 0.75rem;
}

.kpi-filtros__body {
  padding: 0 18px 18px;
}

.kpi-filtros__grid {
  align-items: end;
}

.kpi-filtros__grid--datas {
  margin-bottom: 4px;
}

.kpi-filtros__atalhos {
  gap: 8px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.kpi-filtros__dica-chips {
  margin: 10px 0 12px;
}

.kpi-filtros__linha-injetoras {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 14px 16px;
}

.kpi-filtros__chips-wrap {
  flex: 1 1 auto;
  min-width: 0;
}

.kpi-filtros__acoes-direita {
  flex: 0 0 auto;
  gap: 12px;
}

.kpi-periodo-pill__maq {
  font-weight: 600;
}

.kpi-filtros__erro {
  color: var(--cor-erro);
  margin-top: 10px;
  font-size: 0.9rem;
}

.kpi-pane {
  padding: var(--margem);
}

.kpi-pane--table {
  padding-top: calc(var(--margem) * 0.75);
}

.kpi-periodo-pill {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: var(--margem);
}

.kpi-periodo-pill__ate {
  font-size: 0.85rem;
}

.kpi-periodo-pill__meta {
  margin-left: 4px;
}

.kpi-mosaic {
  margin-bottom: var(--margem);
}

.kpi-tile {
  position: relative;
  padding: calc(var(--margem) * 0.85);
  border-radius: 12px;
  background: var(--cor-bg);
  box-shadow: 0 0 0 1px var(--cor-separador) inset;
  min-height: 108px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.kpi-tile::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  border-radius: 12px 12px 0 0;
  background: var(--cor-separador);
}

.kpi-tile--destaque::before {
  background: linear-gradient(90deg, var(--cor-primaria), var(--cor-primaria-forte));
}

.kpi-tile--perda::before {
  background: linear-gradient(90deg, var(--cor-erro), hsl(32 75% 48%));
  opacity: 0.65;
}

.kpi-tile__label {
  font-size: 0.8rem;
  color: var(--cor-fonte-fraca);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
}

.kpi-tile__value {
  font-size: 1.65rem;
  font-weight: 700;
  color: var(--cor-fonte-forte);
  line-height: 1.15;
}

.kpi-tile__unit {
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--cor-fonte-fraca);
}

.kpi-tile__hint {
  margin-top: 8px;
  font-size: 0.8rem;
}

.kpi-legenda-box {
  padding: 12px 14px;
  border-radius: 8px;
  background: var(--cor-cinza);
  line-height: 1.5;
}

.kpi-legenda-box code {
  font-size: 0.85em;
  padding: 1px 6px;
  border-radius: 4px;
  background: var(--cor-bg);
  box-shadow: 0 0 0 1px var(--cor-separador) inset;
}

.kpi-charts-grid {
  align-items: stretch;
}

.kpi-chart-card {
  padding: calc(var(--margem) / 2);
  border-radius: 10px;
  background: var(--cor-cinza);
  min-width: 0;
}

.kpi-chart-card--wide {
  grid-column: 1 / -1;
}

.kpi-chart-card__title {
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0 0 10px;
  color: var(--cor-fonte-forte);
  text-align: center;
}

.kpi-chart-card__plot {
  height: 300px;
  position: relative;
}

.kpi-chart-card__plot--tall {
  height: 380px;
}

.kpi-table-scroll {
  overflow-x: auto;
  margin: 0 -4px;
}

.kpi-footnote {
  line-height: 1.45;
}

@media screen and (max-width: 970px) {
  .kpi-filtros__linha-injetoras {
    flex-direction: column;
    align-items: stretch;
  }

  .kpi-filtros__acoes-direita {
    width: 100%;
    justify-content: stretch;
  }

  .kpi-filtros__acoes-direita.linha {
    flex-direction: column;
    align-items: stretch;
  }

  .kpi-filtros__acoes-direita button {
    width: 100%;
  }
}
</style>
